import { cx } from './primitives.jsx'

/**
 * Data-display primitives.
 *
 * Tables are the admin console's main surface, so the shape is fixed once here
 * rather than re-invented per collection: a column is `{key, header, render,
 * width, align, numeric}` and the caller supplies rows. `numeric` opts a column
 * into tabular figures so a year or citation column aligns down the page.
 *
 * 数据展示基础组件。
 *
 * 表格是后台的主要界面，因此结构在这里定死一次，而不是每个集合各写一遍：
 * 列的形状是 {key, header, render, width, align, numeric, sortable, sortValue}，
 * 行由调用方提供。numeric 让该列使用等宽数字，保证年份、引用数这类列纵向对齐。
 *
 * Sorting is *offered* here and *performed* by the caller. The table renders the control
 * and reports the click; it never reorders `rows` itself. Which order is correct depends
 * on what the values mean — a date sorts by instant, a status by workflow rank, a title
 * by locale — and a table that guesses gets it wrong for exactly the columns where it
 * matters. `sortValue` lets a column say what it should be compared on.
 *
 * 排序在这里**提供**，由调用方**执行**。表格只渲染控件并上报点击，绝不自己重排 `rows`。
 * 何为正确的顺序取决于值的含义——日期按时刻、状态按流程次序、标题按语言排序规则
 * ——一个自作主张的表格，恰恰会在最要紧的那几列上排错。`sortValue` 让每一列自己声明
 * 该拿什么去比。
 */

/**
 * Data table.
 *
 * @param {object} props Props / 属性。
 * @param {Array<object>} props.columns Column definitions / 列定义。
 * @param {Array<object>} props.rows Row records / 行记录。
 * @param {(row: object) => string} props.rowKey Stable key per row / 每行的稳定 key。
 * @param {(row: object) => void} [props.onRowClick] Row activation / 行点击回调。
 * @param {React.ReactNode} [props.empty] Rendered when there are no rows / 无数据时渲染的内容。
 * @param {string} [props.caption] Accessible table caption / 表格的可访问标题。
 * @param {{key: string, dir: 'asc'|'desc'}} [props.sort] Active sort / 当前排序。
 * @param {(key: string) => void} [props.onSortChange] Header clicked / 表头被点击。
 * @returns {JSX.Element} Table / 表格。
 */
export function DataTable({ columns, rows, rowKey, onRowClick, empty, caption, className, sort, onSortChange }) {
  if (!rows.length && empty) return <div className={cx('ui-table__empty', className)}>{empty}</div>

  return (
    <div className={cx('ui-table-wrap', className)}>
      <table className="ui-table">
        {caption && <caption className="ui-table__caption">{caption}</caption>}
        <thead>
          <tr>
            {columns.map((column) => {
              const active = sort && sort.key === column.key
              const dir = active ? sort.dir : null
              return (
                <th
                  key={column.key}
                  scope="col"
                  className={cx(
                    'ui-table__th',
                    column.align === 'right' && 'is-right',
                    column.numeric && 'is-numeric',
                    column.sortable && 'is-sortable',
                    active && 'is-sorted'
                  )}
                  style={column.width ? { '--col-w': column.width } : undefined}
                  // aria-sort is what tells a screen reader the column is ordered and
                  // which way; the arrow glyph says it only to people who can see it.
                  // aria-sort 才是告诉读屏软件「本列已排序、朝哪个方向」的东西；
                  // 那个箭头字形只对看得见的人说话。
                  aria-sort={column.sortable ? (active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none') : undefined}
                >
                  {column.sortable && onSortChange ? (
                    <button
                      type="button"
                      className="ui-table__sort"
                      onClick={() => onSortChange(column.key)}
                    >
                      {column.header}
                      <span className="ui-table__sort-arrow" aria-hidden="true">
                        {active ? (dir === 'asc' ? '↑' : '↓') : '↕'}
                      </span>
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              className={cx('ui-table__tr', onRowClick && 'is-clickable')}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              // A clickable row must be reachable and activatable by keyboard.
              // 可点击的行必须能被键盘聚焦和触发。
              tabIndex={onRowClick ? 0 : undefined}
              onKeyDown={
                onRowClick
                  ? (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        onRowClick(row)
                      }
                    }
                  : undefined
              }
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={cx(
                    'ui-table__td',
                    column.align === 'right' && 'is-right',
                    column.numeric && 'is-numeric'
                  )}
                >
                  {column.render ? column.render(row) : row[column.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Definition list — label/value pairs, used on the contact and detail pages.
 *
 * 定义列表 —— 标签/值成对展示，用于联系方式页和详情页。
 *
 * @param {object} props Props / 属性。
 * @param {Array<{label: React.ReactNode, value: React.ReactNode}>} props.items Pairs / 键值对。
 * @returns {JSX.Element} Definition list / 定义列表。
 */
export function DefinitionList({ items, className }) {
  return (
    <dl className={cx('ui-dl', className)}>
      {items
        .filter((item) => item && item.value)
        .map((item, index) => (
          <div className="ui-dl__row" key={index}>
            <dt className="ui-dl__label">{item.label}</dt>
            <dd className="ui-dl__value">{item.value}</dd>
          </div>
        ))}
    </dl>
  )
}

/**
 * Flat list of rows separated by hairlines — the shape used for news and
 * publications on the public site.
 *
 * A list, not a grid of cards: cards imply equal weight and waste vertical
 * space, while a hairline-separated list stays scannable at twenty rows a
 * screen.
 *
 * 用发丝线分隔的扁平列表 —— 公开页的新闻和论文都用这个形状。
 *
 * 用列表而不是卡片栅格：卡片暗示各项等权且浪费纵向空间，而发丝线列表在
 * 一屏二十行时依然可快速扫读。
 *
 * @param {object} props Props / 属性。
 * @returns {JSX.Element} Row list / 行列表。
 */
export function RowList({ as: Tag = 'ul', className, children, ...rest }) {
  return (
    <Tag className={cx('ui-rowlist', className)} {...rest}>
      {children}
    </Tag>
  )
}

/**
 * One row inside a `RowList`, laid out as a fixed meta column plus content.
 *
 * RowList 中的一行，布局为「固定元信息列 + 内容」。
 *
 * @param {object} props Props / 属性。
 * @param {React.ReactNode} [props.meta] Left column, e.g. a date / 左列，例如日期。
 * @returns {JSX.Element} Row / 列表行。
 */
export function RowItem({ meta, as: Tag = 'li', className, children, ...rest }) {
  return (
    <Tag className={cx('ui-rowlist__item', className)} {...rest}>
      {meta !== undefined && <div className="ui-rowlist__meta">{meta}</div>}
      <div className="ui-rowlist__content">{children}</div>
    </Tag>
  )
}
